import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Bell, Send, Loader2 } from 'lucide-react';

export const TestNotifications = () => {
  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState('Test Notification');
  const [body, setBody] = useState('This is a test push notification');
  const [leadType, setLeadType] = useState<'sms' | 'contact_form' | 'phone_call'>('contact_form');

  const sendTestNotification = async () => {
    try {
      setLoading(true);

      const { data, error } = await supabase.functions.invoke('send-push-notification', {
        body: {
          title,
          body,
          leadType,
          url: '/admin/all-leads',
          tag: 'test-notification',
        },
      });

      if (error) throw error;

      toast.success(`Test notification sent to ${data.sent} subscriber(s)`);
    } catch (error: any) {
      console.error('Failed to send test notification:', error);
      toast.error('Failed to send test notification: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Bell className="h-5 w-5" />
          <CardTitle>Test Push Notifications</CardTitle>
        </div>
        <CardDescription>
          Send a test notification to all subscribed devices. Make sure you've enabled notifications first.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="notification-title">Notification Title</Label>
          <Input
            id="notification-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Enter notification title"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="notification-body">Notification Body</Label>
          <Textarea
            id="notification-body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Enter notification message"
            rows={3}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="lead-type">Lead Type</Label>
          <Select value={leadType} onValueChange={(value: any) => setLeadType(value)}>
            <SelectTrigger id="lead-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="sms">SMS Message</SelectItem>
              <SelectItem value="contact_form">Contact Form</SelectItem>
              <SelectItem value="phone_call">Phone Call</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Button 
          onClick={sendTestNotification} 
          disabled={loading || !title || !body}
          className="w-full"
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Sending...
            </>
          ) : (
            <>
              <Send className="mr-2 h-4 w-4" />
              Send Test Notification
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
};
